import { useEffect, useRef, useState, type ReactNode } from "react";

/**
 * 横スクロールする幅広テーブルを、上下 2 本のスクロールバーで操作できるようにする
 * ラッパ。上のダミーバーと下の本体をスクロール同期する。
 * 一番下までスクロールしなくても右端の列 (操作列など) を横スクロールで確認できる。
 */
export function ScrollSyncTable({ children }: { children: ReactNode }) {
  const topRef = useRef<HTMLDivElement>(null);
  const bottomRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);

  // 本体の実幅 (table の scrollWidth) を追跡し、上バーのダミー幅を合わせる
  useEffect(() => {
    const bottom = bottomRef.current;
    if (!bottom) return;
    const target = (bottom.firstElementChild as HTMLElement) ?? bottom;
    const update = () => setWidth(target.scrollWidth || bottom.scrollWidth);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(target);
    return () => ro.disconnect();
  }, []);

  // 2 本のスクロール位置を相互同期 (無限ループ防止に syncing フラグ)
  const syncing = useRef(false);
  const sync = (
    from: React.RefObject<HTMLDivElement>,
    to: React.RefObject<HTMLDivElement>,
  ) => {
    if (syncing.current) return;
    syncing.current = true;
    if (from.current && to.current)
      to.current.scrollLeft = from.current.scrollLeft;
    requestAnimationFrame(() => {
      syncing.current = false;
    });
  };

  return (
    <div>
      <div
        ref={topRef}
        onScroll={() => sync(topRef, bottomRef)}
        className="hscroll-visible overflow-x-auto overflow-y-hidden"
      >
        <div style={{ width, height: 1 }} />
      </div>
      <div
        ref={bottomRef}
        onScroll={() => sync(bottomRef, topRef)}
        className="overflow-x-auto"
      >
        {children}
      </div>
    </div>
  );
}
